import React, { useState, useEffect } from 'react';
import { X, RefreshCw, MessageCircle, User } from 'lucide-react';
import { PT } from './types';

interface Comment {
  id: number;
  author_name: string;
  author_image: string | null;
  message: string;
  commented_at: string | null;
  commented_at_formatted: string | null;
}

interface CommentsModalProps {
  pt: PT;
  onClose: () => void;
}

const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000/api`;

const CommentsModal: React.FC<CommentsModalProps> = ({ pt, onClose }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/monitoring/pt/${pt.id}/comments`);
      const result = await response.json();

      if (result.success) {
        setComments(result.data.comments || []);
      } else {
        setError(result.message || 'Failed to load comments');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncComments = async () => {
    try {
      setSyncing(true);
      setError(null);
      const response = await fetch(`${API_BASE}/monitoring/pt/${pt.id}/comments/sync`, {
        method: 'POST',
      });
      const result = await response.json();

      if (result.success) {
        // Refresh comments after sync
        await fetchComments();
      } else {
        setError(result.message || 'Failed to sync comments');
      }
    } catch (err) {
      setError('Failed to sync comments');
      console.error('Error syncing comments:', err);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [pt.id]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 rounded-t-xl">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                <MessageCircle className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Audit Comments</h2>
                <p className="text-blue-100 text-sm">{pt.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={syncComments}
                disabled={syncing}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-lg transition disabled:opacity-50"
                title="Sync from Auditra"
              >
                <RefreshCw size={20} className={syncing ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={onClose}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchComments}
                className="mt-2 text-blue-600 hover:underline text-sm"
              >
                Try again
              </button>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500 mb-2">No comments yet</p>
              <button
                onClick={syncComments}
                disabled={syncing}
                className="text-blue-600 hover:underline text-sm"
              >
                {syncing ? 'Syncing...' : 'Sync from Auditra'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {comment.author_image ? (
                        <img
                          src={comment.author_image.startsWith('http')
                            ? comment.author_image
                            : `https://sistem.auditra.id${comment.author_image}`}
                          alt={comment.author_name}
                          className="w-10 h-10 rounded-full object-cover bg-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center ${comment.author_image ? 'hidden' : ''}`}>
                        <User className="text-blue-500" size={20} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-800 truncate">
                          {comment.author_name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                          {comment.commented_at_formatted || 'Unknown date'}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap break-words">
                        {comment.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {comments.length} comment{comments.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={onClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsModal;
